import React, { useRef, useEffect } from 'react';
import SummarizedResultItem from './SummarizedResultItem';
import Pagination from '../pagination/Pagination';
import PropTypes from 'prop-types';
import styles from './results.module.css';

const propTypes = {
  studyGroups: PropTypes.objectOf(
    PropTypes.shape({
      results: PropTypes.arrayOf(PropTypes.object),
      fullCount: PropTypes.number,
      page: PropTypes.number,
    })
  ).isRequired,
  updateCallback: PropTypes.func.isRequired,
  searchFlag: PropTypes.bool.isRequired,
  filteredKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  onPageChange: PropTypes.func.isRequired,
  pageChangeLoading: PropTypes.object.isRequired,
  searchErrors: PropTypes.object.isRequired,
  perPage: PropTypes.number.isRequired,
};

export default function SummarizedResults({
  studyGroups,
  filteredKeys,
  updateCallback,
  onPageChange,
  pageChangeLoading,
  searchErrors,
  perPage,
}) {
  const detailRefs = {
    sysrev: useRef(null),
    clintri: useRef(null),
    obser: useRef(null),
    casrep: useRef(null),
    pensum: useRef(null),
  };

  const renderStudyList = (studies) =>
    studies.map((study) => (
      <li key={study._id}>
        <SummarizedResultItem
          study={study}
          isOpen={false}
          filteredKeys={filteredKeys}
          updateCallback={updateCallback}
        />
      </li>
    ));

  useEffect(() => {
    const handleHashChange = () => {
      const hashValue = window.location.hash.slice(1);
      if (hashValue && detailRefs[hashValue] && detailRefs[hashValue].current) {
        detailRefs[hashValue].current.open = true;
        detailRefs[hashValue].current.scrollIntoView();
      }
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  return (
    <>
      {!!studyGroups[1].results.length && (
        <details
          open={false}
          ref={detailRefs['sysrev']}
          aria-label="systematic review search results"
          role="tablist"
        >
          <summary className={`${styles.sysHeader} ${styles.summarySection}`}>
            <span style={{ backgroundColor: 'purple', color: 'purple', display: 'inline-block' }}>
              &nbsp;
            </span>
            <span style={{ marginLeft: '2px' }}>
              <strong>Systematic Reviews and/or Meta-Analyses</strong>
            </span>
          </summary>
          {renderStudyList(studyGroups[1].results)}
          {searchErrors[1] && <p className={styles.error}>{searchErrors[1]}</p>}
          <Pagination
            fullCount={studyGroups[1].fullCount}
            perPage={perPage}
            currentPage={studyGroups[1].page}
            onPageChange={(newPage) => onPageChange(1, newPage)}
            disabled={!!pageChangeLoading[1]}
          />
        </details>
      )}
      {!!studyGroups[2].results.length && (
        <details
          open={false}
          ref={detailRefs['clintri']}
          aria-label="clinical trial search results"
          role="tablist"
        >
          <summary className={`${styles.clinHeader} ${styles.summarySection}`}>
            <span style={{ backgroundColor: '#9c9b65', color: '#9c9b65', display: 'inline-block' }}>
              &nbsp;
            </span>
            <span style={{ marginLeft: '2px' }}>
              <strong>Clinical Trials</strong>
            </span>
          </summary>
          {renderStudyList(studyGroups[2].results)}
          {searchErrors[2] && <p className={styles.error}>{searchErrors[2]}</p>}
          <Pagination
            fullCount={studyGroups[2].fullCount}
            perPage={perPage}
            currentPage={studyGroups[2].page}
            onPageChange={(newPage) => onPageChange(2, newPage)}
            disabled={!!pageChangeLoading[2]}
          />
        </details>
      )}
      {!!studyGroups[3].results.length && (
        <details
          open={false}
          ref={detailRefs['obser']}
          aria-label="observational study search results"
          role="tablist"
        >
          <summary className={`${styles.obsHeader} ${styles.summarySection}`}>
            <span style={{ backgroundColor: '#68689c', color: '#68689c', display: 'inline-block' }}>
              &nbsp;
            </span>
            <span style={{ marginLeft: '2px' }}>
              <strong>Observational Studies</strong>
            </span>
          </summary>
          {renderStudyList(studyGroups[3].results)}
          {searchErrors[3] && <p className={styles.error}>{searchErrors[3]}</p>}
          <Pagination
            fullCount={studyGroups[3].fullCount}
            perPage={perPage}
            currentPage={studyGroups[3].page}
            onPageChange={(newPage) => onPageChange(3, newPage)}
            disabled={!!pageChangeLoading[3]}
          />
        </details>
      )}
      {!!studyGroups[4].results.length && (
        <details
          open={false}
          ref={detailRefs['casrep']}
          aria-label="case report search results"
          role="tablist"
        >
          <summary className={`${styles.caseHeader} ${styles.summarySection}`}>
            <span style={{ backgroundColor: '#f69022', color: '#f69022', display: 'inline-block' }}>
              &nbsp;
            </span>
            <span style={{ marginLeft: '2px' }}>
              <strong>Case Reports/Series</strong>
            </span>
          </summary>
          {renderStudyList(studyGroups[4].results)}
          {searchErrors[4] && <p className={styles.error}>{searchErrors[4]}</p>}
          <Pagination
            fullCount={studyGroups[4].fullCount}
            perPage={perPage}
            currentPage={studyGroups[4].page}
            onPageChange={(newPage) => onPageChange(4, newPage)}
            disabled={!!pageChangeLoading[4]}
          />
        </details>
      )}
      {!!studyGroups[5].results.length && (
        <details
          open={false}
          ref={detailRefs['pensum']}
          aria-label="search results pending summarization"
          role="tablist"
        >
          <summary className={`${styles.pendHeader} ${styles.summarySection}`}>
            <span style={{ backgroundColor: '#311140', color: '#311140', display: 'inline-block' }}>
              &nbsp;
            </span>
            <span style={{ marginLeft: '2px' }}>
              <strong>Pending Summary</strong>
            </span>
          </summary>
          {renderStudyList(studyGroups[5].results)}
          {searchErrors[5] && <p className={styles.error}>{searchErrors[5]}</p>}
          <Pagination
            fullCount={studyGroups[5].fullCount}
            perPage={perPage}
            currentPage={studyGroups[5].page}
            onPageChange={(newPage) => onPageChange(5, newPage)}
            disabled={!!pageChangeLoading[5]}
          />
        </details>
      )}
    </>
  );
}

SummarizedResults.propTypes = propTypes;
